import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CheckoutForm({ amountLabel, onPaid, t }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (confirmErr) {
      setError(confirmErr.message || t('publicInvoice.paymentFailed'));
      setSubmitting(false);
      return;
    }
    if (paymentIntent && ['succeeded', 'processing'].includes(paymentIntent.status)) {
      onPaid(paymentIntent.status);
      return;
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="stripe-payment-form" className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <p className="text-xs text-red-500 font-medium" data-testid="stripe-payment-error">{error}</p>}
      <Button
        type="submit"
        data-testid="confirm-payment-btn"
        disabled={!stripe || submitting}
        className="w-full bg-[#003366] hover:bg-[#004080] text-white py-3 text-base rounded-xl shadow-lg disabled:opacity-50"
      >
        {submitting ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('publicInvoice.processing')}</>
        ) : (
          <><CreditCard className="w-5 h-5 mr-2" /> {t('publicInvoice.payNow')} {amountLabel}</>
        )}
      </Button>
    </form>
  );
}

export default function InvoicePaymentForm({ invoice, balance, amountLabel, onPaid, t }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState(null);

  const startPayment = async () => {
    setInitializing(true);
    setError(null);
    try {
      const cfg = await (await fetch(`${API_URL}/api/stripe/config`)).json();
      if (!cfg.configured || !cfg.publishable_key) {
        setError(t('publicInvoice.paymentServiceUnavailable'));
        setInitializing(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round((balance || 0) * 100),
          description: `Invoice ${invoice.invoice_number} - Blue Well Drilling`,
          invoice_id: invoice.id,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || t('publicInvoice.paymentFailed'));
        setInitializing(false);
        return;
      }
      const { client_secret } = await res.json();
      setStripePromise(loadStripe(cfg.publishable_key));
      setClientSecret(client_secret);
    } catch {
      setError(t('publicInvoice.paymentServiceUnavailable'));
    } finally {
      setInitializing(false);
    }
  };

  if (clientSecret && stripePromise) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="invoice-payment-section">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <CreditCard className="w-4 h-4 text-[#003366]" /> {t('publicInvoice.payWithCardAch')}
        </div>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#003366' } } }}
        >
          <CheckoutForm amountLabel={amountLabel} onPaid={onPaid} t={t} />
        </Elements>
        <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3 h-3" /> {t('publicInvoice.securePayment')}
        </p>
      </div>
    );
  }

  return (
    <div className="text-center pt-4 space-y-3">
      <Button
        data-testid="pay-invoice-btn"
        onClick={startPayment}
        disabled={initializing}
        className="bg-[#003366] hover:bg-[#004080] text-white px-8 py-3 text-base rounded-xl shadow-lg"
      >
        {initializing ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('publicInvoice.processing')}</>
        ) : (
          <><CreditCard className="w-5 h-5 mr-2" /> {t('publicInvoice.payNow')} {amountLabel}</>
        )}
      </Button>
      {error && <p className="text-xs text-red-500 font-medium" data-testid="payment-init-error">{error}</p>}
      <p className="text-xs text-gray-400">{t('publicInvoice.payWithCardAch')}</p>
    </div>
  );
}
